import { usePageMeta } from "../seo";
import { Check, ArrowRight } from "lucide-react";
import { CtaBand } from "../components/BookCta";
import { Reveal } from "../components/motion";
import { HONEYBOOK_URL } from "../data/site";

const PACKAGES = [
    {
        name: "The Intimate Date Night",
        price: "From $325",
        tagline: "Outdoor date nights in Dallas & McKinney for two",
        guests: "2 guests · 2 hours",
        features: [
            "Low handcrafted wooden table & plush floor cushions",
            "Layered rugs, throws and ambient LED candles",
            "Styled tablescape with refined florals",
            "Bluetooth speaker & curated playlist",
            "Full setup, styling and breakdown",
        ],
        testid: "package-date-night",
        featured: false,
    },
    {
        name: "The Celebration Soirée",
        price: "From $525",
        tagline: "Birthday picnic setups & bridal shower styling in DFW",
        guests: "Up to 12 guests · 3 hours",
        features: [
            "Everything in The Intimate Date Night",
            "Extended low table styling for your group",
            "Custom color palette & theme design",
            "Welcome sign & place card styling",
            "Balloon garland or floral accent installation",
            "Chilled sparkling beverage station",
        ],
        testid: "package-celebration",
        featured: true,
    },
    {
        name: "The Grand Proposal",
        price: "From $850",
        tagline: "Luxury proposal setups in McKinney & Dallas-Fort Worth",
        guests: "2 guests · sunset session",
        features: [
            "Everything in The Intimate Date Night",
            "Marry Me sign & rose petal aisle",
            "Hidden coordination & timing plan",
            "Champagne toast for two",
            "Keepsake styled floral arrangement",
            "30-minute photography add-on available",
        ],
        testid: "package-proposal",
        featured: false,
    },
];

const ADD_ONS = [
    "Professional photographer",
    "Custom fresh florals",
    "Balloon garland styling",
    "Marquee letters & signage",
    "Luxury picnic baskets to keep",
    "Extra hour of celebration",
];

export default function Packages() {
    usePageMeta(
        "Packages | Luxury Event Styling in McKinney, TX",
        "Three styling tiers for proposals, showers, birthdays, date nights and anniversaries in McKinney and Dallas-Fort Worth — every escape quoted personally after a short inquiry."
    );

    return (
        <main data-testid="packages-page">
            <section className="py-20 sm:py-28">
                <div className="max-w-6xl mx-auto px-5 sm:px-8">
                    <Reveal className="max-w-2xl mb-16">
                        <p className="font-accent text-xs uppercase tracking-[0.25em] text-[#C18E8B] mb-4">
                            Packages & Pricing
                        </p>
                        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#2D230A] leading-tight">
                            Three ways to escape, each one quoted personally
                        </h1>
                        <p className="mt-5 text-[#6E5B3D] leading-relaxed">
                            Every package includes delivery, full setup, styling and complete
                            breakdown anywhere in McKinney, Dallas, Frisco, Plano and
                            surrounding cities. We don't publish flat rates — every escape is
                            quoted personally after a short inquiry, so your proposal reflects
                            your date, location and vision. You simply arrive and celebrate.
                        </p>
                    </Reveal>

                    <div className="grid md:grid-cols-3 gap-6">
                        {PACKAGES.map((p, i) => (
                            <Reveal key={p.name} delay={i * 0.14} className="h-full">
                                <div
                                    data-testid={p.testid}
                                    className={`relative rounded-3xl p-8 h-full flex flex-col border transition-all duration-500 hover:shadow-2xl hover:-translate-y-1.5 ${
                                        p.featured
                                            ? "bg-[#554011] text-[#FAF7F2] border-[#554011] shadow-lg"
                                            : "bg-white border-[#E8C4C1] hover:border-[#C18E8B]"
                                    }`}
                                >
                                    {p.featured && (
                                        <span className="absolute -top-3 left-8 bg-[#FFD2CE] text-[#3D2E0A] px-4 py-1 rounded-full font-accent text-[10px] uppercase tracking-[0.2em] font-semibold">
                                            Most Loved
                                        </span>
                                    )}
                                    <h2 className="font-display text-2xl sm:text-3xl">{p.name}</h2>
                                    <p className={`mt-2 text-sm leading-relaxed ${p.featured ? "text-[#FAF7F2]/75" : "text-[#6E5B3D]"}`}>
                                        {p.tagline}
                                    </p>
                                    <p className={`mt-5 font-display text-3xl italic ${p.featured ? "text-[#FFD2CE]" : "text-[#C18E8B]"}`}>
                                        Tailored quote
                                    </p>
                                    <p className={`mt-1 font-accent text-[11px] uppercase tracking-[0.18em] ${p.featured ? "text-[#FFD2CE]" : "text-[#C18E8B]"}`}>
                                        {p.guests}
                                    </p>
                                    <ul className="mt-7 space-y-3 flex-1">
                                        {p.features.map((f) => (
                                            <li key={f} className="flex items-start gap-3 text-sm leading-relaxed">
                                                <Check className={`w-4 h-4 mt-0.5 shrink-0 ${p.featured ? "text-[#FFD2CE]" : "text-[#C18E8B]"}`} />
                                                <span className={p.featured ? "text-[#FAF7F2]/90" : "text-[#2D230A]/85"}>{f}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    <a
                                        href={HONEYBOOK_URL}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid={`${p.testid}-book-button`}
                                        className={`group mt-8 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-accent text-xs uppercase tracking-[0.18em] transition-all duration-300 hover:scale-[1.03] ${
                                            p.featured
                                                ? "bg-[#FFD2CE] text-[#3D2E0A] hover:bg-[#FAF7F2]"
                                                : "bg-[#554011] text-[#FAF7F2] hover:bg-[#3D2E0A]"
                                        }`}
                                    >
                                        Book This Package
                                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                    </a>
                                </div>
                            </Reveal>
                        ))}
                    </div>

                    <div className="mt-24" data-testid="addons-section">
                        <Reveal>
                            <h2 className="font-display text-3xl sm:text-4xl tracking-tight text-[#2D230A] mb-3">
                                Elevate it further
                            </h2>
                            <p className="text-[#6E5B3D] mb-9 max-w-xl">
                                Add-ons can be included with any package — mention them in
                                your inquiry and we'll include tailored pricing in your proposal.
                            </p>
                        </Reveal>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {ADD_ONS.map((a, i) => (
                                <Reveal key={a} delay={i * 0.06}>
                                    <div
                                        data-testid={`addon-${a.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                        className="bg-white border border-[#E8C4C1] rounded-2xl p-6 flex items-center justify-between hover:border-[#C18E8B] hover:-translate-y-0.5 transition-all duration-300"
                                    >
                                        <span className="text-[#2D230A]">{a}</span>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <CtaBand
                heading="Not sure which package fits?"
                subtext="Send us your occasion, date and guest count — we'll recommend the perfect setup for your celebration."
            />
        </main>
    );
}
